import React, { useMemo } from "react";
import { Autocomplete, Stack, SxProps, TextField, Theme, Typography } from "@mui/material";
import User, { Role } from "../../models/User";
import { copyTextToClipboard } from "../../utils/copyTextToClipboard";

export interface EmployeeAutocompleteProps {
  /** All users from context; only admin + employee are listed. */
  users: User[];
  /** Selected assignee, or `null` for unassigned. */
  value: User | null;
  onChange: (user: User | null) => void | Promise<void>;
  /** Caption above the field; default "Assigned To". Ignored when `hideCaption`. */
  label?: string;
  /** When true, no caption row — use `textFieldLabel` on the input instead. */
  hideCaption?: boolean;
  /** Label on the TextField when `hideCaption` is true. */
  textFieldLabel?: string;
  /** Placeholder when empty; default "Unassigned". */
  placeholder?: string;
  disabled?: boolean;
  sx?: SxProps<Theme>;
  /** Extra styles on the inner TextField (e.g. incomplete-field highlight). */
  textFieldSx?: SxProps<Theme>;
  /** Focus the input on mount (work-queue click-to-edit). */
  autoFocus?: boolean;
  /** Fired when the input blurs (work-queue click-to-edit exit). */
  onBlur?: () => void;
}

/** Display name used for Order Information Received By / Assigned To. */
export function employeeDisplayName(user: User): string {
  const fromName = user.name?.trim();
  if (fromName) return fromName;
  const fromParts = `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim();
  if (fromParts) return fromParts;
  return (user.userName ?? "").trim();
}

/** Logged-in admin/employee display name, or empty when there is no session user. */
export function sessionEmployeeDisplayName(
  sessionUser: User | null | undefined,
  users: User[]
): string {
  if (!sessionUser || sessionUser.id <= 0) {
    return "";
  }
  const fromUsers = users.find(
    (u) =>
      u.id === sessionUser.id &&
      (u.role === Role.ADMIN || u.role === Role.EMPLOYEE)
  );
  return employeeDisplayName(fromUsers ?? sessionUser);
}

/** Match a stored Received By string to a selectable employee user. */
export function findEmployeeByDisplayName(
  users: User[],
  displayName: string,
): User | null {
  const target = displayName.trim().toLowerCase();
  if (!target) return null;
  const employees = users.filter(
    (u) => u.role === Role.ADMIN || u.role === Role.EMPLOYEE,
  );
  return (
    employees.find((u) => employeeDisplayName(u).toLowerCase() === target) ??
    null
  );
}

const EmployeeAutocomplete: React.FC<EmployeeAutocompleteProps> = ({
  users,
  value,
  onChange,
  label = "Assigned To",
  hideCaption = false,
  textFieldLabel,
  placeholder = "Unassigned",
  disabled = false,
  sx,
  textFieldSx,
  autoFocus = false,
  onBlur,
}) => {
  const baseOptions = useMemo(
    () =>
      [...users]
        .filter(
          (u) => u.role === Role.ADMIN || u.role === Role.EMPLOYEE
        )
        .sort((a, b) =>
          employeeDisplayName(a)
            .toLowerCase()
            .localeCompare(employeeDisplayName(b).toLowerCase())
        ),
    [users]
  );

  const options = useMemo(() => {
    if (!value) return baseOptions;
    if (baseOptions.some((u) => u.id === value.id)) return baseOptions;
    return [value, ...baseOptions];
  }, [baseOptions, value]);

  const displayText = value ? employeeDisplayName(value) : "";
  const copyEnabled = displayText.length > 0;

  return (
    <Stack sx={{ minWidth: 0, ...sx }}>
      {!hideCaption && (
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
      )}
      <Autocomplete
        size="small"
        disabled={disabled}
        autoHighlight
        clearOnBlur={false}
        open={autoFocus ? true : undefined}
        options={options}
        value={value}
        onChange={(_, newValue) => {
          void onChange(newValue ?? null);
        }}
        onClose={() => {
          onBlur?.();
        }}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        getOptionLabel={(option) => employeeDisplayName(option)}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="standard"
            fullWidth
            autoFocus={autoFocus}
            label={hideCaption ? textFieldLabel : undefined}
            placeholder={value ? undefined : placeholder}
            sx={textFieldSx}
            title={copyEnabled ? "Double-click to copy" : undefined}
            onBlur={onBlur}
            onDoubleClick={
              copyEnabled
                ? (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    void copyTextToClipboard(displayText);
                  }
                : undefined
            }
          />
        )}
      />
    </Stack>
  );
};

export default EmployeeAutocomplete;
